/**
 * Patches for Select2 bugs.
 *
 */

import * as AttachBody from 'select2/src/js/select2/dropdown/attachBody';
import * as Results from 'select2/src/js/select2/results';
import * as DropdownSearch from 'select2/src/js/select2/dropdown/search';
import * as SelectionSearch from 'select2/src/js/select2/selection/search';
import * as Dropdown from 'select2/src/js/select2/dropdown';

/**
 * Patch for Select2 jumping input bug (https://github.com/select2/select2/issues/4614).
 * Basically extends the attachBody-module by the code of the following PR: https://github.com/select2/select2/pull/5186
 * @TODO: Remove once Select2 gets updated to a version including the PR named above.
 *
 */

let setupResultsEvents = false;
AttachBody.prototype.bind = (function (originalBind) {
    return function (decorated, container, $container) {
        // call the original bind function
        originalBind.call(this, decorated, container, $container);

        // from here on, we add the patch, which is basically an additional
        // event listener for 'results:message'
        container.on('open', () => {
            if (setupResultsEvents) {
                return;
            }
            setupResultsEvents = true;

            container.on('results:message', () => {
                this._positionDropdown();
                this._resizeDropdown();
            });
        });
    }
}(AttachBody.prototype.bind));


/**
 * Patch for Select2 unintentional selection after opening Select2:
 *    If Select2 opens above the select field and the search-option or selectWoo is used, the focus is set the
 *    topmost item in the result list, so the browser immediately scrolls upwards. As Select2 opens on mousedown
 *    and a selection is done with on mouseup, an unintentional selection could happen immediately after open if
 *    the mouse lands on an select option after the scroll.
 *
 * Basically this patch extends the results-module by an additional mouseup listener on the results which prevents
 * the Select2's actions on the first mouseup for a defined time after Select2 opens.
 *
 * However, to allow the behavior of native select inputs (mousedown on select input, move mouse to
 * the desired option, mouseup) the mouseup event is not prevented if the user moved the mouse at least
 * a defined distance.
 */

// Time after selections via mouseup should be enabled again after Select2 opens.
const disableAfterOpen = 1500; // milliseconds

// Minimum distance the mouse has to move to allow the mouseup despite the timeout is not reached yet.
const minimumDistance = 25; // pixels

Results.prototype.bind = (function (originalBind) {
    return function (container, $container) {

        let timer;
        let mouseEnterCoords;
        let disableMouseup = false;

        // reset our mouseup prevention functionality
        const reset = () => {
            clearTimeout(timer);
            disableMouseup = false;
            mouseEnterCoords = null;

            this.$results.off('mousemove.tud.disableMouse');
            this.$results.off('mousedown.tud.disableMouse');
        }

        container.on('open', function (evt) {
            reset();
            disableMouseup = true;

            timer = setTimeout(() => {
                reset();
            }, disableAfterOpen);

            // if the user actively clicks or touches on a Select2 option, then this is not
            // unintended anymore and we can enable mouseup again
            this.$results.one('mousedown.tud.disableMouse', '.select2-results__option[data-selected]', reset);

            // save the initial mouse coordinates
            this.$results.one('mousemove.tud.disableMouse', '.select2-results__option[data-selected]', function(evt) {
                mouseEnterCoords = {
                    x: evt.pageX,
                    y: evt.pageY
                }
            });
        });

        // Listen for mouseup on the results and prevent other listeners from working
        // if Select2 opened quite recently.
        this.$results.on('mouseup', '.select2-results__option[data-selected]', (evt) => {
            if(!disableMouseup) {
                return;
            }

            // calculate the distance of the mouse movement
            let distance = 0;
            if(mouseEnterCoords) {
                distance = Math.sqrt(
                    Math.pow(evt.pageX - mouseEnterCoords.x, 2)
                    + Math.pow(evt.pageY - mouseEnterCoords.y, 2)
                )
            }

            // prevent mouseup if the mouse didn't move enough
            if(distance < minimumDistance) {
                evt.stopImmediatePropagation();
            }

            // Select2 is obviously open now, so we don't need to disable mouseup anymore
            reset();
        });

        // call the original bind function
        originalBind.call(this, container, $container);
    }
}(Results.prototype.bind));

/**
 * Patch for selectWoo that sets the label given in the options as aria-label on search fields
 * @todo Remove this patch once upstream pull request 44 got integrated
 * @see {@link https://github.com/woocommerce/selectWoo/pull/44|selectWoo PR 44}
 */
DropdownSearch.prototype.render = (function (originalRender) {
    return function (decorated) {

        // call the original bind function
        let $rendered = originalRender.call(this, decorated);

        var label = this.options.get( 'label' );
        if ( typeof( label ) === 'string' ) {
            $rendered.find('input.select2-search__field').attr( 'aria-label', label );
        }

        return $rendered;
    }
}(DropdownSearch.prototype.render));

/**
 * Patch for selectWoo that sets the label given in the options as aria-label on multiselect search fields
 * @todo Remove this patch once upstream pull request 44 got integrated
 * @see {@link https://github.com/woocommerce/selectWoo/pull/44|selectWoo PR 44}
 */
SelectionSearch.prototype.render = (function (originalRender) {
    return function (decorated) {

        // call the original render function
        let $rendered = originalRender.call(this, decorated);

        var label = this.options.get( 'label' );
        if ( typeof( label ) === 'string' ) {
            this.$search.attr( 'aria-label', label );
        }
        return $rendered;
    }
}(SelectionSearch.prototype.render));

/**
 * Replace the "select2-result" class as it conflicts with Select2 v3 CSS
 */
Dropdown.prototype.render = (function (originalRender) {
    return function () {
        // call the original render function
        const $dropdown = originalRender.call(this);

        $dropdown.find('.select2-results')
            .removeClass('select2-results')
            .addClass('select2-results-tud');
        return $dropdown;
    };
}(Dropdown.prototype.render));

/**
 * Use our replacement for the "select2-result" class
 */
Results.prototype.position = (function (originalPosition) {
    return function ($results, $dropdown) {
        const $resultsContainer = $dropdown.find('.select2-results-tud');
        $resultsContainer.append($results);
    };
}(Results.prototype.position));

/**
 * Patch for wrong vertical position of the dropdown if a custom dropdownParent (different than the default body element) is used:
 *    selectWoo subtracts the offset of the dropdownParent only from the left position, but not from the top position.
 *    So the dropdown is placed too far down and the browser scrolls down to it as soon as it gets focused. For the
 *    default dropdownParent (<body>) the offset is 0, so nothing changes there.
 *    Upstream Select2 subtracts both offsets, see
 *    https://github.com/select2/select2/blob/4.0.13/src/js/select2/dropdown/attachBody.js
 * @TODO: Remove once selectWoo gets updated to a version subtracting the top offset as well.
 */
AttachBody.prototype._positionDropdown = (function (originalPositionDropdown) {
    return function (...args) {
        originalPositionDropdown.apply(this, args);

        // determine the offset parent the same way as Select2 does
        let $offsetParent = this.$dropdownParent;
        if ($offsetParent.css('position') === 'static') {
            $offsetParent = $offsetParent.offsetParent();
        }

        const top = parseFloat(this.$dropdownContainer.css('top')) - $offsetParent.offset().top;
        this.$dropdownContainer.css('top', top);
    };
}(AttachBody.prototype._positionDropdown));
